import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HTMLElement, parse } from 'node-html-parser'
import { useHandleArticleAnchor } from './useHandleArticleAnchor'
import { useHandleArticleToggle } from './useHandleArticleToggle'
import { useHandleAttachmentSize } from './useHandleAttachmentSize'
import { useHandleGallery } from './useHandleGallery'
import { useHandleIframe } from './useHandleIframe'

const setAttribute = (root: HTMLElement, tags: string[], key: string, value: string) => {
  const textNodes = tags.flatMap((tag) => root.querySelectorAll(tag))

  textNodes.forEach((node) => {
    node.setAttribute(key, value)
  })
}

export const useArticleContent = (content: string, isPublic = false) => {
  useHandleArticleToggle(content)
  useHandleArticleAnchor(content)
  useHandleAttachmentSize(content, isPublic)
  useHandleIframe(content)
  useHandleGallery(content)
  const { i18n } = useTranslation()

  const isRTL = i18n.dir() === 'rtl'

  return useMemo(() => {
    if (!content) return ''

    const root = parse(content)

    const ltrForcedElements = ['table']
    setAttribute(root, ltrForcedElements, 'dir', 'ltr')

    const dirSensitiveElements = ['table tr']
    setAttribute(root, dirSensitiveElements, 'dir', 'auto')

    if (!isRTL) {
      const rtlSensitiveElements = [
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'p',
        'dl',
        'figure.quote',
        '.instructor-info-block',
        '.list',
      ]

      setAttribute(root, rtlSensitiveElements, 'dir', 'auto')
    }

    root.querySelectorAll('iframe').forEach((node) => {
      const { src } = node.attributes ?? {}
      if (!src) return

      const url = new URL(src)
      let langParam: string = ''

      if (url.hostname === 'www.youtube.com') {
        langParam = 'hl'
      } else if (url.hostname === 'player.vimeo.com') {
        langParam = 'texttrack'
      }

      url.searchParams.set(langParam, i18n.language.split('-')[0])
      node.setAttribute('src', url.toString())
    })

    return root.toString()
  }, [content, isRTL, i18n.language])
}
